'use client'

import { clsx } from 'clsx'
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
  forwardRef,
  useMemo,
} from 'react'

import { type GkAirRecommendedProductDataWeb } from '@redteclab/api/clients/crosssellers-proxy-service'

import { useGlobalConfigContext } from '../../../global-config'
import { SliderButton, useSliderNavigation } from '../../../slider'
import { gkAirAccessorGetLocalizedProductUrl } from '../../model/gkAirAccessorGetLocalizedProductUrl'
import { GkAirProductCard } from '../GkAirProductCard/GkAirProductCard'

type GkAirHorizontalSliderProps = Omit<
  ComponentPropsWithoutRef<'div'>,
  'children'
> & {
  onGtmDataLayerPush?: (
    cswProductId: GkAirRecommendedProductDataWeb['UID'],
  ) => void
  products?: GkAirRecommendedProductDataWeb[]
  productsPerPage: number
  slots?: {
    itemContent?: ElementType
  }
}

export const GkAirHorizontalSlider = forwardRef<
  HTMLDivElement,
  GkAirHorizontalSliderProps
>(function GkAirHorizontalSlider(
  {
    className,
    onGtmDataLayerPush,
    products,
    productsPerPage,
    slots = {},
    ...rootElemProps
  },
  ref,
) {
  const { locale } = useGlobalConfigContext()

  const items = useMemo(() => {
    if (!products) {
      /**
       * numbers will be returned to show skeleton items
       */
      return [...Array.from({ length: productsPerPage + 1 }).keys()]
    }

    // Filter out products without url. Not possible to display slider item if there is no product link
    return products.filter((product) =>
      gkAirAccessorGetLocalizedProductUrl({
        locale,
        product,
      }),
    )
  }, [products, productsPerPage, locale])
  const ProductComponent = slots.itemContent ?? GkAirProductCard
  const { containerRef, leftRef, rightRef } =
    useSliderNavigation<HTMLUListElement>()

  return (
    <div
      className={clsx('gk-air-horizontal-slider', className)}
      ref={ref}
      {...rootElemProps}
    >
      <ul
        className={clsx(
          'gk-air-horizontal-slider__slider',
          !products && 'gk-air-horizontal-slider__slider_no-items',
        )}
        ref={containerRef}
        style={
          {
            '--items-per-page': String(productsPerPage),
          } as CSSProperties
        }
      >
        {items.map((item) =>
          typeof item === 'number' ? (
            <li className="gk-air-horizontal-slider__item-wrapper" key={item}>
              <ProductComponent />
            </li>
          ) : (
            <li
              className="gk-air-horizontal-slider__item-wrapper"
              key={item.UID}
              onClick={
                onGtmDataLayerPush
                  ? (event): void => {
                      if (
                        event.target instanceof HTMLElement &&
                        event.target.closest('a')
                      ) {
                        onGtmDataLayerPush(item.UID)
                      }
                    }
                  : undefined
              }
              role="presentation"
            >
              <ProductComponent product={item} />
            </li>
          ),
        )}
      </ul>
      {products !== undefined && products.length > productsPerPage && (
        <>
          <SliderButton
            aria-label="previous"
            className="gk-air-horizontal-slider__navigation-button left-0 hidden tablet:inline-flex"
            direction="left"
            ref={leftRef}
          />
          <SliderButton
            aria-label="next"
            className="gk-air-horizontal-slider__navigation-button right-0 hidden tablet:inline-flex"
            direction="right"
            ref={rightRef}
          />
        </>
      )}
    </div>
  )
})
